import { Face, LandmarkType, selectPrimaryFace } from "../plugins/faceDetection";
import { Capacitor } from "@capacitor/core";
import localforage from "localforage";
import * as faceapi from "face-api.js";

// ============ STORAGE ============

export interface FaceEntry {
  id: string;
  name: string;
  age: number;
  signature: number[];
  createdAt: string;
  updatedAt: string;
  scanCount: number;
  lastSeenAt: string;
}

const getFaceStore = () => {
  return localforage.createInstance({
    name: "face_recognition",
    storeName: "face_entries",
  });
};

export async function getAllFaces(): Promise<FaceEntry[]> {
  try {
    const store = getFaceStore();
    const entries: FaceEntry[] = [];
    await store.iterate<any, void>((value, key) => {
      if (key.startsWith("face_")) entries.push(value);
    });
    return entries.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch {
    return [];
  }
}

export async function saveFaceEntry(entry: FaceEntry): Promise<boolean> {
  try {
    const store = getFaceStore();
    await store.setItem(`face_${entry.id}`, entry);
    return true;
  } catch {
    return false;
  }
}

export async function updateFaceEntry(entry: FaceEntry): Promise<boolean> {
  try {
    const store = getFaceStore();
    entry.updatedAt = new Date().toISOString();
    await store.setItem(`face_${entry.id}`, entry);
    return true;
  } catch {
    return false;
  }
}

export async function deleteFaceEntry(id: string): Promise<boolean> {
  try {
    const store = getFaceStore();
    await store.removeItem(`face_${id}`);
    return true;
  } catch {
    return false;
  }
}

export function generateFaceId(): string {
  return `face_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============ BIOMETRICS ============

export function extractFaceSignature(face: Face): number[] | null {
  if (!face.landmarks) return null;
  const landmarks = face.landmarks;

  const nose = landmarks.find((l) => l.type === LandmarkType.NoseBase);
  const leftEye = landmarks.find((l) => l.type === LandmarkType.LeftEye);
  const rightEye = landmarks.find((l) => l.type === LandmarkType.RightEye);
  const mouthLeft = landmarks.find((l) => l.type === LandmarkType.MouthLeft);
  const mouthRight = landmarks.find((l) => l.type === LandmarkType.MouthRight);

  if (!nose || !leftEye || !rightEye) return null;

  const eyeDist = Math.sqrt(
    Math.pow(leftEye.position.x - rightEye.position.x, 2) +
    Math.pow(leftEye.position.y - rightEye.position.y, 2)
  );
  const scale = eyeDist > 0 ? eyeDist : 1.0;

  const sig: number[] = [];
  const points = [nose, leftEye, rightEye, mouthLeft, mouthRight];

  for (const pt of points) {
    if (pt) {
      sig.push(Number(((pt.position.x - nose.position.x) / scale).toFixed(5)));
      sig.push(Number(((pt.position.y - nose.position.y) / scale).toFixed(5)));
    } else {
      sig.push(0, 0);
    }
  }
  return sig;
}

export function compareFaceSignatures(
  sig1: number[],
  sig2: number[]
): { distance: number; confidence: number } {
  if (!sig1 || !sig2 || sig1.length !== sig2.length || sig1.length === 0) {
    return { distance: 999, confidence: 0 };
  }
  // Euclidean (L2) distance — standard for normalised face embedding vectors
  let sumSq = 0;
  for (let i = 0; i < sig1.length; i++) {
    sumSq += Math.pow(sig1[i] - sig2[i], 2);
  }
  const distance = Math.sqrt(sumSq);
  // Map distance to confidence: distance=0 → 100%, distance≥1.2 → 0%
  const confidence = Math.max(0, Math.round(100 - (distance / 1.2) * 100));
  return { distance, confidence };
}

export function isLookingStraight(face: Face): boolean {
  // Slight tolerance — 27° each side instead of 20°. A web MediaPipe head-pose
  // estimate from only 6 sparse keypoints is noisier than native ML Kit Euler
  // angles, so a tighter gate made a well-centred, near-frontal face intermittently
  // be ignored. The 1s align countdown + multi-frame best capture still prevent
  // capturing an obviously angled/profile face.
  return Math.abs(face.headEulerAngleX || 0) < 27 && Math.abs(face.headEulerAngleY || 0) < 27;
}

/**
 * Find a matching face from storage by comparing signatures.
 */
export async function findMatchingFace(
  signature: number[],
  threshold: number = 70
): Promise<{ entry: FaceEntry; confidence: number } | null> {
  try {
    const entries = await getAllFaces();
    let bestMatch: { entry: FaceEntry; confidence: number } | null = null;
    for (const entry of entries) {
      const result = compareFaceSignatures(signature, entry.signature);
      if (result.confidence > threshold && (!bestMatch || result.confidence > bestMatch.confidence)) {
        bestMatch = { entry, confidence: result.confidence };
      }
    }
    return bestMatch;
  } catch {
    return null;
  }
}

// ============ FACE-API.JS FRONTEND INTEGRATION ============

let modelsLoaded = false;
export async function loadFaceApiModels() {
  // console.log("FaceClockInClockOut: loadFaceApiModels starting");
  if (modelsLoaded) {
    // console.log("FaceClockInClockOut: Models already loaded");
    return;
  }
  // Load from public/models, respecting PUBLIC_URL alias. Normalize the base the
  // same way as faceDetectionWeb.ts/dispatch.ts so a trailing slash in PUBLIC_URL
  // (e.g. http://localhost:3000/) can never produce a double-slash //models path.
  const MODEL_URL = (process.env.PUBLIC_URL || "").replace(/\/+$/, "") + "/models";
  // console.log(`FaceClockInClockOut: Loading face-api.js models from ${MODEL_URL}`);
  await Promise.all([
    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
  ]);
  modelsLoaded = true;
  // console.log("FaceClockInClockOut: [face-api.js] Models loaded successfully.");
}

// Tracks whether the full pipeline warm-up has already run.
// Every step in the real analyzeFace pipeline has first-time overhead:
//   1. fixImageRotation  → first canvas create / drawImage / toDataURL
//   2. enhanceLowLightImage → first getImageData / putImageData / toDataURL
//   3. SSD Mobilenet V1 → first face detection tensor graph execution
//   4. FaceLandmark68Net → first 68-point landmark tensor graph execution & shader compilation
//   5. FaceRecognitionNet → first 128-D descriptor tensor graph execution & shader compilation
//   6. userStorage.getAllUsers + JSON parsing + compareFaceSignatures loop → first IndexedDB & JIT loop
//   7. getPendingRecords + attendanceQueueManager → cold IndexedDB queue access
// We pre-exercise ALL of these explicitly during the upfront loader
// so the first real face scan runs completely smooth without any lag.
let hasWarmedUp = false;
export async function warmUpFaceApiModels(): Promise<void> {
  if (hasWarmedUp) return;
  hasWarmedUp = true; // set eagerly so concurrent calls don't double-warm
  try {
    // console.log("FaceClockInClockOut: Warm-up — running complete analyzeFace pipeline with dummy image...");

    // Ensure all 3 models are loaded in memory
    // await loadFaceApiModels();

    // ── Step 1: Build a realistic dummy camera frame (640×480).
    const WARM_W = 640;
    const WARM_H = 480;
    const offscreen = document.createElement("canvas");
    offscreen.width = WARM_W;
    offscreen.height = WARM_H;
    const ctx = offscreen.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#1a1a1a"; // dark background like a real low-light frame
      ctx.fillRect(0, 0, WARM_W, WARM_H);
      // Face-coloured ellipse in centre — mimics a frontal face region
      ctx.fillStyle = "#c8a882";
      ctx.beginPath();
      ctx.ellipse(WARM_W / 2, WARM_H / 2, 110, 140, 0, 0, Math.PI * 2);
      ctx.fill();
      // Eye regions
      ctx.fillStyle = "#3a2a1a";
      ctx.beginPath();
      ctx.ellipse(WARM_W / 2 - 38, WARM_H / 2 - 30, 22, 14, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(WARM_W / 2 + 38, WARM_H / 2 - 30, 22, 14, 0, 0, Math.PI * 2);
      ctx.fill();
      // Mouth region
      ctx.fillStyle = "#8b4a4a";
      ctx.beginPath();
      ctx.ellipse(WARM_W / 2, WARM_H / 2 + 50, 30, 12, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Get base64 string exactly like the native plugin returns (no data: prefix)
    const warmDataUrl = offscreen.toDataURL("image/jpeg", 0.92);
    const dummyBase64 = warmDataUrl.replace(/^data:image\/jpeg;base64,/, "");

    // ── Step 2: fixImageRotation — warms canvas create/drawImage/toDataURL
    const rotated = await fixImageRotation(dummyBase64);

    // ── Step 3: enhanceLowLightImage — warms getImageData/putImageData/toDataURL
    const enhanced = await enhanceLowLightImage(rotated);

    // ── Step 4: Explicitly warm up ALL THREE neural networks in TensorFlow.js
    // We execute each net directly on the image so none are skipped if detection is empty.
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = async () => {
        try {
          // 4a. SSD Mobilenet V1
          const ssdOptions = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.25 });
          await faceapi.detectAllFaces(img, ssdOptions);

          // 4b. FaceLandmark68Net (forced forward pass to compile landmark shaders)
          await faceapi.nets.faceLandmark68Net.detectLandmarks(img);

          // 4c. FaceRecognitionNet (forced forward pass to compile 128D descriptor shaders)
          await faceapi.nets.faceRecognitionNet.computeFaceDescriptor(img);
        } catch (err) {
          // console.log("FaceClockInClockOut: warm-up net forward pass:", err);
        }
        resolve();
      };
      img.onerror = () => resolve();
      img.src = enhanced.startsWith("data:image") ? enhanced : `data:image/jpeg;base64,${enhanced}`;
    });

    // ── Step 5: userStorage.getAllUsers + JSON parsing + compareFaceSignatures
    try {
      const { userStorage } = await import("./userStorage");
      const users = await userStorage.getAllUsers();
      const dummySignature = new Array(128).fill(0.05);

      if (users && users.length > 0) {
        for (const u of users) {
          if (!u.face_signature) continue;
          try {
            const sig = JSON.parse(u.face_signature);
            if (sig && Array.isArray(sig)) {
              compareFaceSignatures(dummySignature, sig);
            }
          } catch (e) {}
        }
      }
    } catch { /* non-fatal */ }

    // ── Step 6: getPendingRecords + attendanceQueueManager — warms IndexedDB queue
    try {
      const { getPendingRecords } = await import("./attendanceQueueStorage");
      await getPendingRecords();
    } catch { /* non-fatal */ }

    try {
      const { attendanceQueueManager } = await import("./attendanceQueueManager");
      attendanceQueueManager.getOptimisticStatus("warmup_dummy_user");
    } catch { /* non-fatal */ }

    // console.log("FaceClockInClockOut: Full pipeline warm-up complete. First real face scan will be instant.");
  } catch (e) {
    // Non-fatal — the real scan still works, just with first-time latency.
    console.warn("FaceClockInClockOut: Warm-up failed (non-fatal):", e);
    hasWarmedUp = false; // allow retry on next mount
  }
}


export interface IFaceExtractionResult {
  descriptor: number[];
  score: number;
}

export async function processFaceImageFrontend(
  base64Image: string
): Promise<IFaceExtractionResult> {
  // console.log("FaceClockInClockOut: processFaceImageFrontend invoked");
  try {
    await loadFaceApiModels();
  } catch (err: any) {
    console.error("FaceClockInClockOut: Error loading models:", err);
    throw new Error(`Failed to initialize face-api.js models: ${err.message || 'Network error'}. Ensure /models path is accessible.`);
  }
  
  return new Promise((resolve, reject) => {
    // console.log("FaceClockInClockOut: Creating image for face-api processing");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = async () => {
      // console.log("FaceClockInClockOut: Image loaded, starting detection");
      try {
        const ssdOptions = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.25 });
        let detections = await faceapi
          .detectAllFaces(img, ssdOptions)
          .withFaceLandmarks()
          .withFaceDescriptors();

        if (!detections || detections.length === 0) {
          // console.log("FaceClockInClockOut: processFaceImageFrontend Error: No face detected");
          throw new Error("No face detected in the image. Please capture a clear photo of your face.");
        }

        const imageWidth = img.naturalWidth || img.width;
        const imageHeight = img.naturalHeight || img.height;

        // Multi-face policy (FACE_MULTI_FACE_POLICY in plugins/faceDetection.ts):
        // extract from the MOST PROMINENT (largest = closest) face and ignore
        // background / half-visible faces. Only comparably prominent faces are
        // treated as a genuine "multiple faces" problem, so a person walking
        // behind the user never blocks the scan — and the descriptor is always
        // extracted from the original (closest) face only.
        const detectionInfos = detections.map((detection) => ({
          det: detection,
          bounds: {
            left: detection.detection.box.x,
            top: detection.detection.box.y,
            right: detection.detection.box.x + detection.detection.box.width,
            bottom: detection.detection.box.y + detection.detection.box.height,
          },
        }));
        const { primary, ambiguous } = selectPrimaryFace(detectionInfos, imageWidth, imageHeight);
        if (ambiguous) {
          console.log(
            `FaceClockInClockOut: processFaceImageFrontend Error: Multiple prominent faces detected (${detections.length})`,
          );
          throw new Error(
            "Multiple faces detected in the image. Please ensure only one person is in front of the camera and try again.",
          );
        }
        const face = (primary ?? detectionInfos[0]).det;
        const scorePct = Math.round(face.detection.score * 100);

        const box = face.detection.box;
        const boxCenterX = (box.x + box.width / 2) / imageWidth;
        const boxCenterY = (box.y + box.height / 2) / imageHeight;
        const landmarks = face.landmarks.positions;
        // Lenient framing — a face-api detection that found a single face with a
        // reasonable confidence is "good enough" for descriptor extraction. The
        // plugin + 1s countdown already handled framing; don't double-reject a
        // well-centred head that happens to sit a touch off-centre.
        // Lenient framing — a face-api detection that found a single face with a
        // reasonable confidence is "good enough" for descriptor extraction. The
        // plugin + 1s align countdown + low-light normalization all already shield
        // against poorly-framed captures; this just avoids double-rejecting a
        // well-centred head that happens to sit a touch off-centre.
        const hasRequiredLandmarks = landmarks.length >= 3;
        const isInsideFrame =
          box.x / imageWidth >= 0.01 &&
          box.y / imageHeight >= 0.01 &&
          (box.x + box.width) / imageWidth <= 0.99 &&
          (box.y + box.height) / imageHeight <= 0.99;
        const isCentered =
          Math.abs(boxCenterX - 0.5) <= 0.30 &&
          Math.abs(boxCenterY - 0.5) <= 0.30;
        if (!hasRequiredLandmarks) {
          throw new Error("Face details are not clearly visible. Please move a bit closer and try again.");
        }
        if (!isInsideFrame) {
          throw new Error("Face is not fully inside the frame. Please center your face fully and try again.");
        }
        if (!isCentered) {
          throw new Error("Face is off-centre. Please look straight into the centre of the camera and try again.");
        }

        const descriptor = Array.from(face.descriptor);
        if (descriptor.length !== 128) {
          // console.log(`FaceClockInClockOut: Error: Invalid descriptor length (${descriptor.length})`);
          throw new Error("Invalid face descriptor length extracted from image.");
        }

        // console.log("FaceClockInClockOut: processFaceImageFrontend Extraction Success");
        resolve({
          descriptor,
          score: scorePct,
        });
      } catch (err) {
        console.error("FaceClockInClockOut: processFaceImageFrontend Error during extraction:", err);
        reject(err);
      }
    };
    img.onerror = (e) => {
      console.error("FaceClockInClockOut: Failed to load image for face processing", e);
      reject(new Error("Failed to load image for face processing"));
    };
    
    if (base64Image.startsWith("data:image")) {
      img.src = base64Image;
    } else {
      img.src = `data:image/jpeg;base64,${base64Image}`;
    }
  });
}

// ============ ML KIT NATIVE 128D SIGNATURE ============

/**
 * Builds a 128-element feature vector from ML Kit face data.
 * Uses landmarks, bounding box ratios, head angles and probabilities.
 * Runs instantly — no TensorFlow inference required.
 */
export function extractMLKitSignature(face: Face): number[] | null {
  if (!face.landmarks || face.landmarks.length === 0) {
    console.warn("[extractMLKitSig] No landmarks available in Face object.");
    return null;
  }

  const landmarks = face.landmarks;
  const bounds = face.bounds;

  // Helper: find landmark by type, return {x:0, y:0} if missing
  const getLM = (type: LandmarkType) => {
    const lm = landmarks.find((l) => l.type === type);
    return lm ? lm.position : { x: 0, y: 0 };
  };

  const nose      = getLM(LandmarkType.NoseBase);
  const leftEye   = getLM(LandmarkType.LeftEye);
  const rightEye  = getLM(LandmarkType.RightEye);
  const mouthL    = getLM(LandmarkType.MouthLeft);
  const mouthR    = getLM(LandmarkType.MouthRight);
  const mouthB    = getLM(LandmarkType.MouthBottom);
  const leftCheek = getLM(LandmarkType.LeftCheek);
  const rightCheek= getLM(LandmarkType.RightCheek);
  const leftEar   = getLM(LandmarkType.LeftEar);
  const rightEar  = getLM(LandmarkType.RightEar);

  // Normalisation scale = inter-ocular distance
  const eyeDx = leftEye.x - rightEye.x;
  const eyeDy = leftEye.y - rightEye.y;
  const scale = Math.sqrt(eyeDx * eyeDx + eyeDy * eyeDy) || 1;

  // Face centre for relative coordinates
  const cx = (leftEye.x + rightEye.x) / 2;
  const cy = (leftEye.y + rightEye.y) / 2;

  // Normalise a point relative to face centre and eye distance
  const n = (pt: { x: number; y: number }) => [
    (pt.x - cx) / scale,
    (pt.y - cy) / scale,
  ];

  // ---- 10 keypoints × 2 coords = 20 features ----
  const kpFeatures: number[] = [
    ...n(nose),
    ...n(leftEye),
    ...n(rightEye),
    ...n(mouthL),
    ...n(mouthR),
    ...n(mouthB),
    ...n(leftCheek),
    ...n(rightCheek),
    ...n(leftEar),
    ...n(rightEar),
  ]; // 20 values

  // ---- Pairwise distances between key points (normalised) = 45 pairs ----
  const allPts = [nose, leftEye, rightEye, mouthL, mouthR, mouthB, leftCheek, rightCheek, leftEar, rightEar];
  const distFeatures: number[] = [];
  for (let i = 0; i < allPts.length; i++) {
    for (let j = i + 1; j < allPts.length; j++) {
      const dx = allPts[i].x - allPts[j].x;
      const dy = allPts[i].y - allPts[j].y;
      distFeatures.push(Math.sqrt(dx * dx + dy * dy) / scale);
    }
  } // 45 values

  // ---- Bounding box shape ratios = 5 features ----
  const bw = (bounds.right - bounds.left) || 1;
  const bh = (bounds.bottom - bounds.top) || 1;
  const boxFeatures: number[] = [
    bw / bh,                                           // aspect ratio
    (nose.x - bounds.left) / bw,                      // nose x within box
    (nose.y - bounds.top)  / bh,                       // nose y within box
    (leftEye.x - bounds.left) / bw,
    (rightEye.x - bounds.left) / bw,
  ]; // 5 values

  // ---- Head pose angles (normalised to [-1, 1]) = 3 features ----
  const poseFeatures: number[] = [
    (face.headEulerAngleX || 0) / 90,
    (face.headEulerAngleY || 0) / 90,
    (face.headEulerAngleZ || 0) / 90,
  ]; // 3 values

  // ---- Classification probabilities = 3 features ----
  const classFeatures: number[] = [
    face.smilingProbability      ?? 0,
    face.leftEyeOpenProbability  ?? 0,
    face.rightEyeOpenProbability ?? 0,
  ]; // 3 values

  // ---- Contour point count as a ratio (if available) = 1 feature ----
  const contourFeatures: number[] = [
    face.contours ? face.contours.reduce((s, c) => s + c.points.length, 0) / 200 : 0,
  ]; // 1 value

  // Total so far: 20 + 45 + 5 + 3 + 3 + 1 = 77
  // Pad to 128 with angle-to-landmark cross features
  const crossFeatures: number[] = [];
  const angleY = (face.headEulerAngleY || 0) / 90;
  for (const pt of allPts) {
    crossFeatures.push(((pt.x - cx) / scale) * angleY);
    crossFeatures.push(((pt.y - cy) / scale) * angleY);
    if (crossFeatures.length >= 51) break;
  } // up to 51 values — totals 77 + 51 = 128

  const sig = [
    ...kpFeatures,
    ...distFeatures,
    ...boxFeatures,
    ...poseFeatures,
    ...classFeatures,
    ...contourFeatures,
    ...crossFeatures,
  ].slice(0, 128);

  // Pad to exactly 128 if short
  while (sig.length < 128) sig.push(0);

  console.log(`[extractMLKitSig] Signature built: ${sig.length} dims from ${landmarks.length} landmarks`);
  return sig;
}



export async function fixImageRotation(base64Image: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // ===== WEB =====
      // On web, FaceDetectionWeb.captureFrame() already standardizes the frame
      // to a 480x640 portrait (3:4 aspect ratio) natural un-mirrored image
      // with image enhancement applied. No rotation or re-encoding is needed —
      // re-encoding would cause a second JPEG compression that degrades
      // facial detail needed for face-api.js descriptor extraction.
      if (!Capacitor.isNativePlatform()) {
        // Return the original image as-is to avoid quality loss from
        // double JPEG compression.
        resolve(base64Image.startsWith("data:image") ? base64Image : `data:image/jpeg;base64,${base64Image}`);
        return;
      }

      // ===== NATIVE (Android / iOS) =====
      // After the native plugin fixes, captured frames should already be
      // upright portrait images. This function serves as a safety net:
      // if a frame is still landscape (width > height), it means the native
      // rotation was not applied (e.g. rotationDegrees was 0 on some devices).
      // We rotate 90 degrees clockwise to bring the face upright.
      const isLandscape = img.width > img.height;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(base64Image.startsWith("data:image") ? base64Image : `data:image/jpeg;base64,${base64Image}`);
        return;
      }

      if (isLandscape) {
        // Landscape frame — rotate 90 degrees clockwise to portrait.
        // This is the most common correction needed for raw sensor frames
        // that were not rotated by the native plugin.
        canvas.width = img.height;
        canvas.height = img.width;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
      } else {
        // Image is already portrait/upright — use as-is.
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }

      const rotatedBase64 = canvas.toDataURL("image/jpeg", 0.95);
      resolve(rotatedBase64);
    };
    img.onerror = () => reject(new Error("Failed to load image for rotation"));

    // Ensure it's a valid data URL
    if (base64Image.startsWith("data:image")) {
      img.src = base64Image;
    } else {
      img.src = `data:image/jpeg;base64,${base64Image}`;
    }
  });
}

// ============ IMAGE QUALITY VALIDATION ============

export interface ImageQualityResult {
  isSharp: boolean;
  isWellLit: boolean;
  sharpnessScore: number;
  brightnessScore: number;
}

/**
 * Validates image quality (sharpness + brightness) to reject blurry or
 * poorly-lit captures that would produce unreliable face descriptors.
 * This is critical for low-quality webcams where blurry frames are common.
 */
export function validateImageQuality(base64Image: string): Promise<ImageQualityResult> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.min(img.width, 320); // Downscale for speed
        canvas.height = Math.min(img.height, 240);
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          resolve({ isSharp: true, isWellLit: true, sharpnessScore: 100, brightnessScore: 100 });
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // 1. Sharpness: compute Laplacian variance (higher = sharper)
        let sum = 0;
        let sumSq = 0;
        let count = 0;
        const w = canvas.width;
        const h = canvas.height;
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            const idx = (y * w + x) * 4;
            const gray = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
            const up = 0.299 * data[((y - 1) * w + x) * 4] + 0.587 * data[((y - 1) * w + x) * 4 + 1] + 0.114 * data[((y - 1) * w + x) * 4 + 2];
            const down = 0.299 * data[((y + 1) * w + x) * 4] + 0.587 * data[((y + 1) * w + x) * 4 + 1] + 0.114 * data[((y + 1) * w + x) * 4 + 2];
            const left = 0.299 * data[(y * w + (x - 1)) * 4] + 0.587 * data[(y * w + (x - 1)) * 4 + 1] + 0.114 * data[(y * w + (x - 1)) * 4 + 2];
            const right = 0.299 * data[(y * w + (x + 1)) * 4] + 0.587 * data[(y * w + (x + 1)) * 4 + 1] + 0.114 * data[(y * w + (x + 1)) * 4 + 2];
            const laplacian = 4 * gray - up - down - left - right;
            sum += laplacian;
            sumSq += laplacian * laplacian;
            count++;
          }
        }
        const mean = sum / count;
        const variance = sumSq / count - mean * mean;
        // Normalize: variance of ~100+ is sharp, < 20 is very blurry
        const sharpnessScore = Math.min(100, Math.max(0, Math.round((variance / 100) * 100)));

        // 2. Brightness: average luminance
        let lumSum = 0;
        for (let i = 0; i < data.length; i += 4) {
          lumSum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        }
        const avgLum = lumSum / (data.length / 4);
        // Ideal brightness is ~128 (mid-gray). Score 100 at 128, drops off at extremes.
        const brightnessScore = Math.max(0, Math.min(100, Math.round(100 - Math.abs(avgLum - 128) * 0.8)));

        resolve({
          isSharp: sharpnessScore >= 25,
          isWellLit: brightnessScore >= 30,
          sharpnessScore,
          brightnessScore,
        });
      } catch (err) {
        // If validation fails, don't block the flow
        resolve({ isSharp: true, isWellLit: true, sharpnessScore: 100, brightnessScore: 100 });
      }
    };
    img.onerror = () => {
      resolve({ isSharp: true, isWellLit: true, sharpnessScore: 100, brightnessScore: 100 });
    };
    if (base64Image.startsWith("data:image")) {
      img.src = base64Image;
    } else {
      img.src = `data:image/jpeg;base64,${base64Image}`;
    }
  });
}

/**
 * Hard quality gate for the app-side (native) face capture path.
 *
 * The web flow already validates quality before capturing/registering (face-api.js
 * descriptor + quality checks), but the native app relied only on picking the best
 * of a few frames, so a motion-blurred frame could slip through when the user moved
 * at the last moment. This helper runs AFTER capture against the exact image that
 * will be saved/sent, and throws a clear user-facing error when the face is too
 * blurry to be reliable — so a blurry photo can never be registered or matched.
 *
 * Deliberately LENIENT so users can't get stuck in a capture loop (the existing
 * align countdown, straight-head gate and multi-frame best-capture already do most
 * of the framing work). We ONLY reject frames whose Laplacian-variance edge energy
 * falls below the same `isSharp` threshold already used elsewhere in the app — a
 * user holding reasonably still passes trivially. Brightness is intentionally NOT
 * gated here because the low-light normalizer (and the daylight pass-through in
 * enhanceLowLightImage) already handle exposure.
 */
export async function assertClearFaceCapture(
  base64Image: string,
): Promise<ImageQualityResult> {
  const quality = await validateImageQuality(base64Image);
  if (!quality.isSharp) {
    throw new Error(
      "Blurry face detected. Please hold still and keep your face clearly in the frame, then try again.",
    );
  }
  return quality;
}
// ============ LOW-LIGHT ENHANCEMENT ============

/**
 * Smart low-light normalizer used BEFORE face-api.js descriptor extraction and
 * before sending a captured frame to the backend for matching / registration.
 *
 * Night & low-light camera frames are dark/noisy, which makes the face-api.js
 * landmark/descriptor step (and its backend equivalent) produce a weaker vector
 * that drifts away from the good daytime signature — so matching/confidence drops
 * below threshold at night.
 *
 * When the image's average luminance is low, this lifts the shadows (gamma),
 * stretches contrast and re-sharpens. When the frame is already reasonably lit
 * (daytime), it returns the ORIGINAL unchanged so we never degrade a good image
 * with an avoidable second JPEG re-encode.
 */
export const LOW_LIGHT_LUMA_THRESHOLD = 70;

export async function enhanceLowLightImage(base64Image: string): Promise<string> {
  const toDataUrl = (v: string) =>
    v.startsWith("data:image") ? v : `data:image/jpeg;base64,${v}`;
  const src = toDataUrl(base64Image);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          resolve(src);
          return;
        }

        // 1. Analyze average luminance on a small downscale (fast).
        const AW = Math.min(w, 320);
        const AH = Math.max(1, Math.round(h * (AW / w)));
        const probe = document.createElement("canvas");
        probe.width = AW;
        probe.height = AH;
        const pctx = probe.getContext("2d", { willReadFrequently: true });
        if (!pctx) {
          resolve(src);
          return;
        }
        pctx.drawImage(img, 0, 0, AW, AH);
        const probeData = pctx.getImageData(0, 0, AW, AH).data;
        let lumSum = 0;
        const pxCount = probeData.length / 4;
        for (let i = 0; i < probeData.length; i += 4) {
          lumSum += 0.299 * probeData[i] + 0.587 * probeData[i + 1] + 0.114 * probeData[i + 2];
        }
        const avgLum = lumSum / pxCount;

        // Well-lit image — use as-is to avoid double JPEG compression.
        if (avgLum >= LOW_LIGHT_LUMA_THRESHOLD) {
          resolve(src);
          return;
        }

        // 2. Dark image — normalize at full resolution.
        const full = document.createElement("canvas");
        full.width = w;
        full.height = h;
        const ctx = full.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          resolve(src);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        // 2a. Gamma lift (gamma < 1 brightens shadows more than highlights).
        const gamma = 0.6;
        const g = (v: number) => 255 * Math.pow(v / 255, gamma);

        // 2b. Contrast stretch using luminance min/max (guarded) so a lone
        //     hot highlight doesn't blow out the whole stretch.
        let lo = 255;
        let hi = 0;
        for (let i = 0; i < data.length; i += 4) {
          const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          if (l < lo) lo = l;
          if (l > hi) hi = l;
        }
        const range = hi - lo;
        const doStretch = range > 0 && range < 235;
        const scale = doStretch ? 255 / range : 1;
        const offset = doStretch ? -lo * scale : 0;

        const out = new Uint8ClampedArray(data.length);
        for (let i = 0; i < data.length; i += 4) {
          let r = g(data[i]);
          let gg = g(data[i + 1]);
          let b = g(data[i + 2]);
          if (doStretch) {
            r = r * scale + offset;
            gg = gg * scale + offset;
            b = b * scale + offset;
          }
          out[i] = Math.max(0, Math.min(255, r));
          out[i + 1] = Math.max(0, Math.min(255, gg));
          out[i + 2] = Math.max(0, Math.min(255, b));
          out[i + 3] = data[i + 3];
        }

        // 2c. Light 3x3 unsharp-mask sharpen to restore facial-feature edges.
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            const idx = (y * w + x) * 4;
            for (let c = 0; c < 3; c++) {
              const center = out[idx + c];
              const up = out[((y - 1) * w + x) * 4 + c];
              const down = out[((y + 1) * w + x) * 4 + c];
              const left = out[(y * w + (x - 1)) * 4 + c];
              const right = out[(y * w + (x + 1)) * 4 + c];
              const avg = (up + down + left + right) / 4;
              out[idx + c] = Math.max(0, Math.min(255, center + (center - avg) * 0.3));
            }
          }
        }

        imageData.data.set(out);
        ctx.putImageData(imageData, 0, 0);
        resolve(full.toDataURL("image/jpeg", 0.92));
      } catch (err) {
        // Never block the flow over enhancement — fall back to the original.
        console.warn("[LowLight] Enhancement skipped:", err);
        resolve(src);
      }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}
